import './index.css';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { keyboardMatch, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { BottomSheet, BottomSheetFooter } from '@hh.ru/magritte-ui-bottom-sheet';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { Button } from '@hh.ru/magritte-ui-button';
import { Drop } from '@hh.ru/magritte-ui-drop';
import { ArrowPathOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { NavigationBar } from '@hh.ru/magritte-ui-navigation-bar';
import { r as renderSection, s as styles } from './sectionRenderers-BGy3XMzM.js';
import '@hh.ru/magritte-ui-card';
import '@hh.ru/magritte-ui-cell';
import '@hh.ru/magritte-ui-chips';
import '@hh.ru/magritte-ui-typography';

const getSearchResultCount = (options) => options?.filter(({ type, searchable = true }) => type !== 'delimiter' && searchable).length || 0;
const dropPlacement = ['bottom-left', 'top-left'];
const SuggestPicker = function ({ data, activatorRef, inputRef, onValueSelect, maxHeight, navigationBarProps, input, inputFocused, currentInputValueSelectedFromSuggest, suggestContainerRef, itemsGap, onBottomSheetClose, onBottomSheetOpen, inputValue, topContent, bottomContent, dropWidth, isError, trls, errorPlaceholder, onClickReload, onDropOpen, onDropClose, onSearchSubmit, renderItem, }) {
    const { isMobile, breakpoint } = useBreakpoint();
    const [showBottomSheet, setShowBottomSheet] = useState(false);
    const [showDrop, setShowDrop] = useState(true);
    const bottomSheetInputRef = useRef(null);
    const [caretVisible, setCaretVisible] = useState(false);
    const renderItemRef = useRef(renderItem);
    renderItemRef.current = renderItem;
    useEffect(() => {
        if (inputFocused) {
            setShowBottomSheet(true);
        }
        else if (!isMobile) {
            setShowBottomSheet(false);
        }
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [inputFocused, setShowBottomSheet]);
    useEffect(() => {
        if (currentInputValueSelectedFromSuggest) {
            setShowBottomSheet(false);
        }
    }, [currentInputValueSelectedFromSuggest, isMobile]);
    // если скрыли дроп из-за скрытия активатора под скроллом, то при следующем изменении данных нужно его показать
    useEffect(() => {
        setShowDrop(true);
    }, [inputValue, inputFocused]);
    const onKeyDownHandler = useCallback((event) => {
        input.props.onKeyDown?.(event);
        if (onSearchSubmit && keyboardMatch(event.nativeEvent, keyboardKeys.Enter)) {
            const onCloseBottomSheet = () => {
                setShowBottomSheet(false);
            };
            // в onSearchSubmit нужно вернуть количество результатов поиска
            onSearchSubmit(event, onCloseBottomSheet, getSearchResultCount(data));
        }
    }, [input.props, onSearchSubmit, data]);
    const suggestContent = useMemo(() => {
        if (!data) {
            return null;
        }
        return data.reduce((result, section, index) => {
            const renderResult = renderSection(section, index, onValueSelect, breakpoint, data.length, Boolean(bottomContent), renderItemRef.current);
            result.push(...renderResult);
            return result;
        }, []);
    }, [data, onValueSelect, breakpoint, bottomContent]);
    const dropVisible = suggestContent !== null &&
        suggestContent.length > 0 &&
        !currentInputValueSelectedFromSuggest &&
        inputFocused &&
        showDrop;
    const InputComponent = input.component;
    const processInput = (ref) => (event) => {
        const hasPrintableRepresentation = event.key.replace(/\s/g, '').length === 1;
        if (!hasPrintableRepresentation) {
            return;
        }
        ref.current?.focus();
        const newEvent = new KeyboardEvent('keydown', event.nativeEvent);
        ref.current?.dispatchEvent(newEvent);
    };
    return (jsxs(Fragment, { children: [jsxs(BottomSheet, { visible: showBottomSheet, height: "full-screen", onAfterExit: onBottomSheetClose, keyboardOverlaysFooter: false, header: jsx(NavigationBar, { ...navigationBarProps, options: jsx("div", { className: classnames({
                            [styles.bottomSheetInputHideCaret]: !caretVisible,
                        }), children: jsx(InputComponent, { ...{
                                ...input.props,
                                invalid: false,
                                ref: bottomSheetInputRef,
                                autoComplete: 'off',
                                onKeyDown: onKeyDownHandler,
                            } }) }) }), onAppear: () => {
                    bottomSheetInputRef?.current?.focus();
                    setCaretVisible(true);
                    onBottomSheetOpen?.();
                }, onBeforeExit: () => {
                    bottomSheetInputRef?.current?.blur();
                    setCaretVisible(false);
                }, onClose: () => {
                    setShowBottomSheet(false);
                }, footer: isError && (jsx(BottomSheetFooter, { children: jsx(Button, { mode: "secondary", style: "negative", icon: jsx(ArrowPathOutlinedSize24, {}), onClick: onClickReload, "data-qa": "suggest-reload-button", stretched: true, children: trls.resetButton }) })), ref: suggestContainerRef, allowScrollWhileFocused: true, children: [!isError && (jsxs("div", { onKeyDown: processInput(bottomSheetInputRef), className: styles.suggestItemsContainer, style: { gap: itemsGap }, children: [topContent, suggestContent, bottomContent] })), isError && jsx("div", { className: styles.errorWrapper, children: errorPlaceholder })] }), jsx(Drop, { autoFocusWhenOpened: false, placement: dropPlacement, lockPlacement: true, visible: dropVisible, onClose: () => setShowDrop(false), activatorRef: activatorRef, role: "status", space: 400, widthEqualToActivator: !dropWidth, width: dropWidth, forcePlacement: true, keyboardNavigationMode: "list", "data-qa": "suggest-drop", ref: suggestContainerRef, onAppear: onDropOpen, onAfterExit: onDropClose, children: jsx("div", { onKeyDown: processInput(inputRef), className: styles.suggestItemsContainer, style: { maxHeight }, children: jsxs("div", { className: styles.suggestItemsContainerWrapper, style: { gap: itemsGap }, children: [topContent, suggestContent, bottomContent] }) }) })] }));
};
SuggestPicker.displayName = 'SuggestPicker';

export { SuggestPicker, getSearchResultCount };
//# sourceMappingURL=SuggestPicker.js.map
